import { createContext, type ReactNode, useContext } from 'react';
import { panelMaximized } from '@/lib/desktop-shell/app-panels';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { activePanelView } from '@/lib/side-panel/panel-tabs';

const FloatingConversationContext = createContext(false);

export function useFloatingConversation() {
  return useContext(FloatingConversationContext);
}

/** The classes of the layer a floating conversation sits in, over a fullscreen view. */
const FLOATING_CONVERSATION_LAYER =
  'desktop-floating-conversation pointer-events-none absolute inset-x-4 bottom-4 z-40 mx-auto flex max-w-[860px] flex-col';

/** Presents the conversations below as a floating composer over a fullscreen view. */
function FloatingConversationProvider({
  floating,
  children,
}: {
  floating: boolean;
  children: ReactNode;
}) {
  return <FloatingConversationContext value={floating}>{children}</FloatingConversationContext>;
}

/** Change presentation without remounting the conversation or its composer. */
export function FloatingConversation({ children }: { children: ReactNode }) {
  const maximized = useDesktopShellStore(panelMaximized);
  const view = useDesktopShellStore((state) => activePanelView(state.panelTabs));
  // A maximized panel shows a conversation of its own (chat, subagent) or floats this one over it.
  const floating = maximized && view !== 'chat' && view !== 'subagent';

  return (
    <FloatingConversationProvider floating={floating}>
      <div
        aria-hidden={(maximized && !floating) || undefined}
        inert={(maximized && !floating) || undefined}
        className={
          maximized && !floating
            ? 'hidden'
            : floating
              ? FLOATING_CONVERSATION_LAYER
              : 'relative flex min-h-0 min-w-0 flex-1'
        }
      >
        {children}
      </div>
    </FloatingConversationProvider>
  );
}
